import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCallback } from 'react';

type AuditEventType = 
  | 'login_success'
  | 'login_failed'
  | 'login_blocked'
  | 'login_locked'
  | 'logout'
  | 'unauthorized_access_attempt'
  | 'user_created'
  | 'user_updated'
  | 'user_deleted'
  | 'user_status_changed'
  | 'permission_changed'
  | 'group_access_changed'
  | 'work_access_changed'
  | 'tenant_created'
  | 'tenant_updated'
  | 'tenant_deleted'
  | 'work_created'
  | 'work_updated'
  | 'work_deleted'
  | 'inspection_created'
  | 'inspection_updated'
  | 'inspection_deleted'
  | 'inspection_voided'
  | 'template_created'
  | 'template_updated'
  | 'template_deleted'
  | 'service_deleted'
  | 'group_deleted'
  | 'template_reverted';

interface AuditLogParams {
  eventType: AuditEventType;
  userId?: string;
  resourceType: string;
  resourceId?: string;
  details?: Record<string, any>;
}

// Edge function URL for server-side audit logging
const getAuditLogUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  return `https://${projectId}.supabase.co/functions/v1/log-audit-event`;
};

export function useAuditLog() {
  const { profile } = useAuth();

  const logEvent = useCallback(async ({
    eventType,
    userId,
    resourceType,
    resourceId,
    details,
  }: AuditLogParams) => {
    try {
      // Get current session for auth header
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      // Call edge function for server-side IP capture
      const response = await fetch(getAuditLogUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          eventType,
          userId: userId || profile?.id,
          resourceType,
          resourceId,
          details,
        }),
      });

      if (!response.ok) {
        console.error('Failed to log audit event:', await response.text());
      }
    } catch (error) {
      console.error('Failed to log audit event:', error);
    }
  }, [profile?.id]);

  return { logEvent };
}

// Standalone function for use in contexts without hooks
export async function logAuditEvent({
  eventType,
  userId,
  resourceType,
  resourceId,
  details,
  accessToken: providedToken,
}: AuditLogParams & { accessToken?: string }) {
  try {
    // Use provided token or get from session
    let accessToken = providedToken;
    if (!accessToken) {
      const { data: sessionData } = await supabase.auth.getSession();
      accessToken = sessionData?.session?.access_token || undefined;
    }

    // Skip audit logging if no auth token (e.g. failed login attempts)
    // These events are already tracked by record_failed_login in the DB
    if (!accessToken) {
      console.warn(`Skipping audit log for '${eventType}': no auth token available`);
      return;
    }

    // Call edge function for server-side IP capture
    const response = await fetch(getAuditLogUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        eventType,
        userId,
        resourceType,
        resourceId,
        details,
      }),
    });

    if (!response.ok) {
      console.error('Failed to log audit event:', await response.text());
    }
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }
}
